<template>
    <vx-modal :visible="props.visible"
        @update:visible="emit('update:visible', $event)"
        :padding="false"
        size="md">
        <template v-slot:header>
            <div class="px-6 pt-6">
                Variables
            </div>
        </template>
        <template v-slot:subheader>
            <div class="px-6 pb-4">
                Variables can be inserted into your message to dynamically render contextual information.
            </div>
            <div class="px-6">
                <vx-input ref="searchInput"
                    v-model="search"
                    size="lg"
                    placeholder="Search">
                    <template v-slot:right>
                        <font-awesome-icon widthAuto class="text-slate-800"
                            :icon="faMagnifyingGlass"></font-awesome-icon>
                    </template>
                </vx-input>
            </div>
        </template>

        <div class="p-6">
            <progress-bar :class="{
                'pb-8': progressStatus === 'completed'
            }" v-model:status="progressStatus" :activeBlock="progressActiveBlock" :blocks="progressBlocks" defaultTitle="Loading variables…"></progress-bar>
            <template v-if="search.length > 0 && !filteredVariablesLength">
                <div class="text-center">
                    No results found
                </div>
            </template>
            <animate-height :height="progressActiveBlock === progressBlocks ? 'auto' : 0" :duration="300">
                <template v-if="filteredVariables.team.length || filteredVariables.team_custom_attributes.length">
                    <variable-title>Team</variable-title>

                    <variable-badges>
                        <template v-if="filteredVariables.team.length">
                            <variable-badge v-for="v in filteredVariables.team"
                                :key="v.label + Math.random()"
                                @click.prevent="select(v.var)">
                                {{ v.label }}
                            </variable-badge>
                        </template>

                        <template v-if="filteredVariables.team_custom_attributes.length">
                            <variable-badge v-for="v in filteredVariables.team_custom_attributes"
                                :key="v.label + Math.random()"
                                @click.prevent="select(v.var)">
                                {{ v.label }}
                            </variable-badge>
                        </template>
                    </variable-badges>
                </template>

                <template v-if="filteredVariables.group.length || filteredVariables.group_custom_attributes.length">
                    <variable-title>Group</variable-title>

                    <variable-badges>
                        <template v-if="filteredVariables.group.length">
                            <variable-badge v-for="v in filteredVariables.group"
                                :key="v.label + Math.random()"
                                @click.prevent="select(v.var)">
                                {{ v.label }}
                            </variable-badge>
                        </template>

                        <template v-if="filteredVariables.group_custom_attributes.length">
                            <variable-badge v-for="v in filteredVariables.group_custom_attributes"
                                :key="v.label + Math.random()"
                                @click.prevent="select(v.var)">
                                {{ v.label }}
                            </variable-badge>
                        </template>
                    </variable-badges>
                </template>

                <template v-if="!FEATURES.local_attributes && (filteredVariables.contact.length || filteredVariables.contact_custom_attributes.length)">
                    <variable-title>Contact</variable-title>

                    <variable-badges>
                        <variable-badge v-for="v in filteredVariables.contact"
                            :key="v.label + Math.random()"
                            @click.prevent="select(v.var)">
                            {{ v.label }}
                        </variable-badge>

                        <variable-badge v-for="v in filteredVariables.contact_custom_attributes"
                            :key="v.label + Math.random()"
                            @click.prevent="select(v.var)">
                            {{ v.label }}
                        </variable-badge>
                    </variable-badges>
                </template>

                <template v-if="FEATURES.local_attributes && filteredVariables.contact_shared.length">
                    <variable-title>Shared Contact Attributes</variable-title>

                    <variable-badges>
                        <variable-badge v-for="v in filteredVariables.contact_shared"
                            :key="v.label + Math.random()"
                            @click.prevent="select(v.var)">
                            {{ v.label }}
                        </variable-badge>
                    </variable-badges>
                </template>

                <template v-if="FEATURES.local_attributes && (filteredVariables.contact_corporate.length || filteredVariables.contact_custom_attributes.length)">
                    <variable-title>Corporate Contact Attributes</variable-title>

                    <variable-badges>
                        <variable-badge v-for="v in filteredVariables.contact_corporate"
                            :key="v.label + Math.random()"
                            @click.prevent="select(v.var)">
                            {{ v.label }}
                        </variable-badge>

                        <variable-badge v-for="v in filteredVariables.contact_custom_attributes"
                            :key="v.label + Math.random()"
                            @click.prevent="select(v.var)">
                            {{ v.label }}
                        </variable-badge>
                    </variable-badges>
                </template>

                <template v-if="FEATURES.local_attributes && (filteredVariables.contact_local.length || filteredVariables.local_custom_attributes.length)">
                    <variable-title>Local Contact Attributes</variable-title>

                    <variable-badges>
                        <variable-badge v-for="v in filteredVariables.contact_local"
                            :key="v.label + Math.random()"
                            @click.prevent="select(v.var)">
                            {{ v.label }}
                        </variable-badge>

                        <variable-badge v-for="v in filteredVariables.local_custom_attributes"
                            :key="v.label + Math.random()"
                            @click.prevent="select(v.var)">
                            {{ v.label }}
                        </variable-badge>
                    </variable-badges>
                </template>

                <template v-if="filteredVariables.promotions.length">
                    <variable-title>Promotions</variable-title>

                    <variable-badges>
                        <variable-badge v-for="(v, index) in filteredVariables.promotions"
                            :key="index"
                            @click.prevent="select(v.var)">
                            {{ v.label }}
                        </variable-badge>
                    </variable-badges>
                </template>

                <template v-if="filteredVariables.external_contact_list.length">
                    <variable-title>External Contact List</variable-title>

                    <variable-badges>
                        <variable-badge v-for="(v, index) in filteredVariables.external_contact_list"
                            :key="index"
                            @click.prevent="select(v.var)">
                            {{ v.label }}
                        </variable-badge>
                    </variable-badges>
                </template>
            </animate-height>
        </div>
    </vx-modal>
</template>

<script setup>
import AnimateHeight from 'vue-animate-height'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-solid-svg-icons';
import { VxModal, VxInput } from '@voxie/frontend-components';
import { computed, h, inject, nextTick, reactive, ref, watch } from 'vue';
import { useToasted } from '../../../../composables/useToasted';
import { customAttributesClient, teamCustomAttributesClient, promotionsClient, groupsClient , externalListsClient } from "~/services";
import ProgressBar from '~/components/general/progress/ProgressBar.vue';
import { useCurrentGroup } from '~/composables/useCurrentGroup';

const VariableTitle = (props, { slots }) => h('h3', {
    class: 'text-slate-700 text-base font-extrabold mb-2',
    'data-test': 'variable-title',
}, [slots.default()]);

const VariableBadge = (props, { slots }) => h('div', {
    class: 'h-5 px-1 bg-teal-100 rounded-sm text-slate-700 text-sm font-normal cursor-pointer hover:text-white hover:bg-teal-500',
    'data-test': 'variable-badge',
}, [slots.default()]);

const VariableBadges = (props, { slots }) => h('div', {
    class: 'flex flex-wrap gap-x-1.5 gap-y-3 mb-4',
}, [slots.default()]);


const toasted = useToasted();
const { onCorporate, getCurrentGroupId, currentGroup, currentOrgUnit } = useCurrentGroup();

const props = defineProps({
    visible: Boolean,
    externalContactLists: Array,
});

const emit = defineEmits(['update:visible', 'selected']);

const teamId = Spark.state.currentTeam.id;
const context = computed(() => ({ teamId, orgUnitId: currentOrgUnit.value?.id, groupId: currentGroup.value?.group_id }));
const FEATURES = inject('FEATURES');

const search = ref('');
const searchInput = ref(null);

watch(() => props.visible, (visible) => {
    if (!visible) {
        return;
    }
    nextTick(() => {
        searchInput.value?.field?.focus();
    });
});

const loaded = ref(false);

const progressActiveBlock = ref(0);
const progressStatus = ref('not-started');
const progressBlocks = computed(() => {
    let blocks = 2;

    if (FEATURES.contact_groups) {
        blocks++;
    }

    if (FEATURES.local_attributes) {
        blocks++;
    }

    if (onCorporate()) {
        blocks++;
    }

    if (FEATURES.external_contact_lists && props.externalContactLists.length > 0) {
        blocks++;
    }

    return blocks;
});

watch(progressActiveBlock, () => {
    if (progressActiveBlock.value === progressBlocks.value) {
        progressStatus.value = 'completed';
    }
});

const variables = reactive({
    team: [
        { label: 'team name', var: '{{team=>name}}' },
    ],

    group: FEATURES.contact_groups ? [
        { label: 'group external id', var: '{{group=>external_id}}' },
        { label: 'group name', var: '{{group=>name}}', },
    ] : [],

    contact: FEATURES.local_attributes ? [] : [
        { label: 'first name', var: '{{contact=>first_name}}' },
        { label: 'last name', var: '{{contact=>last_name}}' },
        { label: 'phone', var: '{{contact=>phone}}' },
        { label: 'email', var: '{{contact=>email}}' },
        { label: 'entry channel', var: '{{contact=>entry_channel}}' },
        { label: 'membership status', var: '{{contact=>membership_status}}' },
        { label: 'lifecycle status', var: '{{contact=>lifecycle_status}}' },
        { label: 'created date', var: '{{contact=>created_at}}' },
        { label: 'updated date', var: '{{contact=>updated_at}}' },
    ],

    contact_shared: FEATURES.local_attributes ? [
        { label: 'first name', var: '{{contact=>first_name}}' },
        { label: 'last name', var: '{{contact=>last_name}}' },
        { label: 'phone', var: '{{contact=>phone}}' },
        { label: 'created date', var: '{{contact=>created_at}}' },
        { label: 'updated date', var: '{{contact=>updated_at}}' },
    ] : [],

    contact_corporate: FEATURES.local_attributes ? [
        { label: 'email', var: '{{contact=>email}}' },
        { label: 'entry channel', var: '{{contact=>entry_channel}}' },
        { label: 'membership status', var: '{{contact=>membership_status}}' },
        { label: 'lifecycle status', var: '{{contact=>lifecycle_status}}' },
    ] : [],

    contact_local: FEATURES.local_attributes ? [
        { label: 'email', var: '{{contact=>local=>email}}' },
        { label: 'entry channel', var: '{{contact=>local=>entry_channel}}' },
        { label: 'membership status', var: '{{contact=>local=>membership_status}}' },
        { label: 'lifecycle status', var: '{{contact=>local=>lifecycle_status}}' },
    ] : [],

    message: [
        { label: 'from', var: '{{message=>from}}' },
    ],

    contact_custom_attributes: [], //loaded via API response

    local_custom_attributes: [], //loaded via API response

    team_custom_attributes: [], //loaded via API response

    group_custom_attributes: [], //loaded via API response

    promotions: [], //loaded via API response

    external_contact_list: [], //loaded via API response
});

const select = (variable) => {
    emit('selected', variable);
    emit('update:visible', false);
}

const filteredVariables = computed(() => {
    const allVariables = {}

    Object.keys(variables).forEach((key) => {
        if (Array.isArray(variables[key])) {
            allVariables[key] = variables[key]
                .filter(v => v.label.toLowerCase().includes(search.value.toLowerCase()))
        } else if (typeof variables[key] === "object") {
            allVariables[key] = {}
            Object.keys(variables[key]).forEach(subKey => {
                allVariables[key][subKey] = variables[key][subKey]
                    .filter(v => v.label.toLowerCase().includes(search.value.toLowerCase()));
            })
        }
    });

    return allVariables;
});

const filteredVariablesLength = computed(() => {
    return Object.keys(filteredVariables.value).reduce((acc, curr) => {
        if (Array.isArray(filteredVariables.value[curr])) {
            return acc + filteredVariables.value[curr].length;
        } else if (typeof filteredVariables.value[curr] === "object") {
            return acc + Object.keys(filteredVariables.value[curr]).reduce((subAcc, subKey) => {
                return subAcc + filteredVariables.value[curr][subKey].length;
            }, 0)
        }
    }, 0)
});

const loadGroupCustomAttributes = async () => {
    if (!FEATURES.contact_groups) {
        return;
    }

    try {
        const response = getCurrentGroupId() ?
            await groupsClient.getGroupCustomAttributes({ teamId }, getCurrentGroupId()) :
            await groupsClient.getGroupCustomAttributesMeta({ teamId });

        if (getCurrentGroupId()) {
            variables.group_custom_attributes = response.data.data.sort((a, b) => a.key > b.key ? 1 : -1)
                .map(attr => {
                    return {
                        label: attr.key,
                        var: `{{group=>custom_attributes=>${attr.key}}}`
                    }
                });
        } else {
            variables.group_custom_attributes = response.data.reduce((acc, next) => {
                return [...acc, { label: next, var: `{{group=>custom_attributes=>${next}}}` }];
            }, []);
        }

        progressActiveBlock.value = progressActiveBlock.value + 1;
    } catch (error) {
        console.error(error);
        toasted.global.platform_error();
    }
}

watch(() => props.visible, () => {
    if (!props.visible || loaded.value) {
        return;
    }

    progressStatus.value = 'running';

    customAttributesClient.customAttributesMeta()
        .then(({ data }) => {
            variables.contact_custom_attributes = data.reduce((acc, next) => {
                const nextValue = next.replace(/[^A-Za-z0-9]/g, '_');

                return [...acc, { label: next, var: `{{contact=>custom_attributes=>${nextValue}}}` }];
            }, []);
            progressActiveBlock.value = progressActiveBlock.value + 1;
        })
        .catch(error => {
            console.error(error);
            toasted.global.platform_error();
        });

    if (FEATURES.local_attributes) {
        customAttributesClient.localCustomAttributesMeta()
            .then(({ data }) => {
                variables.local_custom_attributes = data.reduce((acc, next) => {
                    const nextValue = next.replace(/[^A-Za-z0-9]/g, '_');

                    return [...acc, { label: next, var: `{{contact=>local=>custom_attributes=>${nextValue}}}` }];
                }, []);
                progressActiveBlock.value = progressActiveBlock.value + 1;
            })
            .catch(error => {
                console.error(error);
                toasted.global.platform_error();
            });
    }

    teamCustomAttributesClient.getList(teamId)
        .then(({ data }) => {
            variables.team_custom_attributes = data.data.sort((a, b) => a.key > b.key ? 1 : -1)
                .map(attr => {
                    return {
                        label: attr.key,
                        var: `{{team=>custom_attributes=>${attr.key}}}`
                    }
                });

            progressActiveBlock.value = progressActiveBlock.value + 1;
        })
        .catch(error => {
            console.error(error);
            toasted.global.platform_error();
        });

    // Fetch group custom attributes.
    loadGroupCustomAttributes();

    // Fetch promotions.
    if (onCorporate()) {
        promotionsClient.getAll(teamId)
            .then(response => {
                variables.promotions = response.data.data.sort((a, b) => a.name.localeCompare(b.name))
                    .map(promotion => {
                        return {
                            label: promotion.name,
                            var: `{{promotion=>coupon=>${promotion.code}}}`,
                        };
                    });
                    progressActiveBlock.value = progressActiveBlock.value + 1;
            })
            .catch((error) => {
                console.error(error);
            });
    }

    if (FEATURES.external_contact_lists && props.externalContactLists.length > 0) {
        variables.external_contact_list = [
            {
                label: 'external list name',
                var: '{{external_list=>name}}',
            }
        ];

        externalListsClient.getExternalListsAttributes(context.value, {
            external_contact_list_ids: props.externalContactLists.map(list => list.id),
        })
            .then(response => {
                response.data.data
                    .forEach(attribute => {
                        const escaped = attribute.replace(/[^A-Za-z0-9]/g, '_');
                        variables.external_contact_list.push({
                            label: attribute,
                            var: `{{external_list=>attributes=>${escaped}}}`,
                        });
                    });
                progressActiveBlock.value = progressActiveBlock.value + 1;
            })
            .catch((error) => {
                console.error(error);
                toasted.global.platform_error();
            });
    }

    loaded.value = true;
}, { immediate: true })
</script>
